import { useCallback, useEffect, useState } from "react";

import BlockRounded from "@mui/icons-material/BlockRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";

import BasicModal from "../../components/modals/BasicModal";
import DateInput from "../../components/inputs/DateInput";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { closingCoverageApi } from "../../api/closingCoverageApi";
import { C, MONO } from "./coverageUtils";

const holidayDay = (h) => String(h?.holidayDate || "").slice(0, 10);

// Manage bank-holiday / wire-block dates. Read is open to everyone; mutation
// controls render only when `canManage` is true (server still enforces 403).
const BankHolidayModal = ({ show, onClose, year, canManage, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add/edit form (upsert is keyed by date, so editing = re-submitting a date).
  const [date, setDate] = useState(null);
  const [name, setName] = useState("");
  const [wireBlocked, setWireBlocked] = useState(true);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    closingCoverageApi
      .getBankHolidays({ start: `${year}-01-01`, end: `${year}-12-31` })
      .then((data) =>
        setHolidays(Array.isArray(data?.holidays) ? data.holidays : []),
      )
      .catch(() => setHolidays([]))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const resetForm = () => {
    setDate(null);
    setName("");
    setWireBlocked(true);
    setNote("");
  };

  const editRow = (h) => {
    setDate(h?.holidayDate || null);
    setName(h?.name || "");
    setWireBlocked(h?.wireBlocked !== false);
    setNote(h?.note || "");
  };

  const save = async () => {
    const day = date ? String(date).slice(0, 10) : null;
    if (!day || !name.trim()) {
      enqueueSnackbar("Pick a date and a name.", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      await closingCoverageApi.upsertBankHoliday({
        holidayDate: day,
        name: name.trim(),
        wireBlocked,
        note: note || undefined,
      });
      enqueueSnackbar("Bank holiday saved.", { variant: "success" });
      resetForm();
      load();
      onChanged?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not save the holiday.";
      enqueueSnackbar(String(detail), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (h) => {
    if (!h?._id) return;
    try {
      await closingCoverageApi.deleteBankHoliday(h._id);
      enqueueSnackbar("Bank holiday removed.", { variant: "success" });
      load();
      onChanged?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not delete the holiday.";
      enqueueSnackbar(String(detail), { variant: "error" });
    }
  };

  const sorted = [...holidays].sort((a, b) =>
    holidayDay(a).localeCompare(holidayDay(b)),
  );

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      maxWidth="sm"
      title={`Bank holidays · ${year}`}
    >
      <Box sx={{ pt: 1 }}>
        {canManage && (
          <Box
            sx={{
              p: 1.5,
              mb: 1.5,
              borderRadius: "10px",
              border: `1px solid ${C.line}`,
              background: C.bg,
            }}
          >
            <Flx g={1.5} wrap sx={{ alignItems: "flex-end" }}>
              <Box sx={{ flex: "1 1 150px" }}>
                <Txt
                  sx={{ fontSize: 12, fontWeight: 600, color: C.sub, mb: 0.5 }}
                >
                  Date
                </Txt>
                <DateInput value={date} onChange={setDate} onAccept={setDate} />
              </Box>
              <Box sx={{ flex: "2 1 180px" }}>
                <Txt
                  sx={{ fontSize: 12, fontWeight: 600, color: C.sub, mb: 0.5 }}
                >
                  Name
                </Txt>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Type holiday name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Box>
            </Flx>
            <Flx ac jb g={1} wrap sx={{ mt: 1.25 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={wireBlocked}
                    onChange={(e) => setWireBlocked(e.target.checked)}
                  />
                }
                label={<Txt sx={{ fontSize: 13 }}>Wires blocked</Txt>}
              />
              <TextField
                size="small"
                placeholder="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{ flex: "1 1 200px" }}
              />
              <Button variant="contained" onClick={save} disabled={saving}>
                Save
              </Button>
            </Flx>
          </Box>
        )}

        {loading ? (
          <Txt
            sx={{ fontSize: 12.5, color: C.faint, py: 2, textAlign: "center" }}
          >
            Loading…
          </Txt>
        ) : sorted.length === 0 ? (
          <Txt
            sx={{ fontSize: 12.5, color: C.faint, py: 2, textAlign: "center" }}
          >
            No bank holidays for {year}.
          </Txt>
        ) : (
          <Box
            sx={{
              border: `1px solid ${C.line}`,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            {sorted.map((h, i) => (
              <Flx
                ac
                jb
                g={1}
                key={h._id || i}
                onClick={canManage ? () => editRow(h) : undefined}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderBottom:
                    i < sorted.length - 1 ? `1px solid ${C.lineSoft}` : "none",
                  cursor: canManage ? "pointer" : "default",
                  "&:hover": canManage ? { background: C.bg } : {},
                }}
              >
                <Flx ac g={1} sx={{ minWidth: 0 }}>
                  <Txt
                    sx={{
                      fontFamily: MONO,
                      fontSize: 11.5,
                      color: C.sub,
                      flexShrink: 0,
                      width: 92,
                    }}
                  >
                    {holidayDay(h)}
                  </Txt>
                  <Txt
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: C.ink,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {h.name}
                  </Txt>
                </Flx>
                <Flx ac g={1} sx={{ flexShrink: 0 }}>
                  {h.wireBlocked && (
                    <Flx
                      ac
                      g={0.4}
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: "99px",
                        background: C.blockBg,
                        color: C.block,
                      }}
                    >
                      <BlockRounded sx={{ fontSize: 12 }} />
                      <Txt
                        sx={{ fontSize: 10.5, fontWeight: 700, color: C.block }}
                      >
                        Wires blocked
                      </Txt>
                    </Flx>
                  )}
                  {canManage && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(h);
                      }}
                      sx={{ color: C.faint }}
                    >
                      <DeleteOutlineRounded sx={{ fontSize: 17 }} />
                    </IconButton>
                  )}
                </Flx>
              </Flx>
            ))}
          </Box>
        )}

        <Flx ac end sx={{ mt: 2 }}>
          <Button variant="text" color="gray" onClick={onClose}>
            Close
          </Button>
        </Flx>
      </Box>
    </BasicModal>
  );
};

export default BankHolidayModal;
